/*
 *  Product Thumbnail Print
 *    Exporting common product thumbnail string.
 *
 *
 *  Version:
 *    1.0.0 - 2022/06/30
 */

import { Currency, escapeString, handlize, pictureGenerate, printMoney, t, Product, Variant, CurrencyFormat, VariantImage } from '@process-creative/slate-theme-tools';

type OptionWithValue = {
  name: string;
  values: string[];
  selected_value: string;
  position: 1 | 2 | 3;
}

type ProductObjectUpdated = Product & {
  options_with_values: OptionWithValue[];
  excerpt?: string | null;
  available: boolean;
  url: string;
  badge?: string | null;
  rollover_image?: Array<VariantImage> | null;
  bisButtonLabel?: string;
  abv?: string | null;
  lowstock?: string | null;
}

/**
 * The parameters for the product thumbnail print functionality
 */
export type ProductThumbnailPrintParams = {
  p: ProductObjectUpdated;
  p2: ProductObjectUpdated;
  v: Variant;
  clazz: string;
  is_swatches: boolean | string;
}

/**
 * Creates a product thumbnail based on provided product object data
 * @param params The information necessary to display the product thumbnail
 * @returns string of html to be renedered
 */
export const ProductThumbnailPrint = (params: ProductThumbnailPrintParams) => {
  const { p, p2, v, clazz, is_swatches } = params;

  const win = window as Window & typeof globalThis &
  { Currency: { currency: Currency } };
  const currency: Currency = win.Currency.currency || 'USD';
  const format: CurrencyFormat = 'money_format';

  return /*html*/`
    <div
      class="o-product-thumbnail ${clazz || ''}  ${p.rollover_image ? 'has-hover' : ''}"
      data-product-thumbnail
      data-product-id="${p.id}"
      data-variant-id="${v.id}"
    >
      <div class="o-product-thumbnail__inner">
        
        <a class="o-product-thumbnail__image-container o-media-container" data-product-form-image href="${p.url}">
          ${rolloverImage()}
          ${image()}
        </a>

        ${title()}

        ${excerpt()}

        ${formProduct()}
       
      </div>
    </div>
  `;

  //rollover image on hover
  function rolloverImage() {

    //if rollover image is not set, return empty string
    if(!p.rollover_image) return '';
    
    //We get a two dimensional array of images from metafields, so we need to flatten it
    const array = p.rollover_image.flat();

    //We need to get the image url for the variant we are currently on
    const image = array[0].src;

    return /*html*/`
     ${image ? 
      `<picture>
        <img src="${image}" class="o-product-thumbnail__hover-image o-media" alt="${p.title}">
      </picture>` : ``}
    `;
  }
  
  //product badge
  function badge() {
    return /* html */ `
      ${p.badge ? `
        <div class="o-product-thumbnail__badge o-type--2--caps">
          ${p.badge}
        </div>
      ` : ``}`
  }

  //product image
  function image() {
    return `<picture><img class="o-product-thumbnail__image o-media" src="${p.featured_image || p.image || ''}"></picture>`
      return /* html */`
          ${''/*pictureGenerate({
        class: `o-product-thumbnail__image o-media`,
        src: p.featured_image || p.image || '',
        srcSize: 500,
        sizes: [
          { size: 173, screen: 375 },
          { size: 200, screen: 2000 }
        ]
      })*/}
    `;
  }

  //product title
  function title() {
    return /* html */ `
      <a href="${p.url}" class="o-product-thumbnail__title o-heading--10">
        ${p.title}
      </a>
    `
  }

  //product excerpt
  function excerpt() {
    return /* html */ `
      ${p.excerpt ? `
        ${p.excerpt.includes('<p>') ? `
          <div class="o-product-thumbnail__excerpt">
            ${p.excerpt}
          </div>
      ` : `
        <p class="o-product-thumbnail__excertp">
          ${escapeString(p.excerpt)}
        </p>`}
      ` : ``}
    `
  }

  //product form
  function formProduct() {
    const addLabel = t('products.product.add_to_cart');
    const soldOutLabel = t('products.product.sold_out');
    const preorderLabel = t('products.product.preorder');
    const bisButtonLabel = p.bisButtonLabel;
    const viewLabel = t('products.product.view_product');
    
    // button content for animation
    const buttonInner = /* html */ `
      <span class="c-product-form__add-btn-label" data-product-form-add-label>
        ${v.available && v.inventory_quantity != null && v.inventory_quantity > 0 ? addLabel : v.inventory_policy == 'continue' ? preorderLabel : preorderLabel}
      </span>
      <span class="c-product-form__add-btn-separator">—</span>
      ${pricing()}
    `
    
    //real button with add to cart functionality
    const button = /* html */ `
      <button
        type="submit"
        class="c-product-form__add-btn o-btn is-quaternary has-animation
          ${bisButtonLabel ? 'is-bis-button-label' : ''}
          ${!v.available ? `is-disabled` : ``}"
          ${!v.available ? `disabled="disabled"` : ``}
          data-product-form-add
        >
        <div class="o-btn__text">
          ${buttonInner}
        </div>
        <div class="o-btn__text">
          ${buttonInner}
        </div>
      </button>
      `

    const viewProduct = /* html */ `
      <a href="${p.url}" class="c-product-form__add-btn o-btn is-quaternary has-animation">
      <div class="o-btn__text">
        ${viewLabel}
        <span class="c-product-form__add-btn-separator">—</span>
        ${pricing()}
      </div>
      <div class="o-btn__text">
        ${viewLabel}
        <span class="c-product-form__add-btn-separator">—</span>
        ${pricing()}
      </div>
    </a>
    `

    const buttonInnerLink = /* html */ `
      <span class="c-product-form__add-btn-label">
        ${bisButtonLabel ? bisButtonLabel : soldOutLabel}
      </span>
        ${bisButtonLabel ? `<span class="c-product-form__add-btn-separator">—</span>
        ${pricing()}` : ``}
    `
    // just link if product sold out
    const link = /* html */ `
      <a href="${p.url}" class="c-product-form__add-btn o-btn is-quaternary has-animation is-disabled ${bisButtonLabel ? 'is-bis-button-label' : ''}">
      <div class="o-btn__text">
        ${buttonInnerLink}
      </div>
      <div class="o-btn__text">
        ${buttonInnerLink}
      </div>
    </a>
    `

    return /* html */ `
      <form class="o-product-thumbnail__form" data-product-form>

        ${abvStockBadges()}

        <div class="c-product-form__add-btn-row">
        
        ${v.available && v.inventory_quantity != null && v.inventory_quantity > 0 ? viewProduct : link}
   
        </div>
      </form>
    `;
  }

  //product pricing
  function pricing() {
    const price = v.price;
    const comparePrice = v.compare_at_price ? v.compare_at_price : price;

    return /* html */`
    <p
      class="o-pricing
      c-product-form__pricing
      o-type--btn 
      ${comparePrice > price ? `has-compare` : ``}"
      data-product-pricing
    >
      ${price == 0 ? /* html */`
        <span
          class="o-pricing__money o-pricing__price o-product-thumbnail__pricing-money
          o-product-thumbnail__pricing-price"
          data-currency
          data-product-money
        >
          ${t('products.product.free_price_label')}
        </span>`
        : /* html */ `
        <span
          class="o-pricing__money o-pricing__price o-product-thumbnail__pricing-money
          o-product-thumbnail__pricing-price
          money
          ${comparePrice > price ? `has-compare` : ``}"
          data-currency
          data-money="${price}"
          data-product-money
          data-product-id="${p.id}"
        >
          ${printMoney(comparePrice, format, currency)}
        </span>`
      }
    </p>`
  }

  //product swatches
  function swatches() {
    return /*html*/`
    <div class="c-product-form__swatch-wrapper is-hidden">
      <div
        class="o-swatches c-product-form__swatches is-hidden
          ${hasOnlyDefaultVariant(p) ? `is-hidden` : ``}"
        data-swatches
      >
        ${variantSelector()}
        ${(p.variants.length === 1 && v.title.toLowerCase().includes('default title')) || (!is_swatches) ? `
        ` : /*html*/`
          <div class="o-swatches__swatches has-js">
            ${p.options_with_values.reduce((x, option) => {
      return x += /*html*/`
                <div
                  class="o-swatches__swatch ${optionUseSwatches(option) ? `` : `is-select`}"
                  data-swatch
                  data-swatch-position="${option.position}"
                >
                  <span for=${p.id + '-' + option.name} class="o-swatches__swatch-title o-heading--11">
                    ${t('products.product.product_option_select')}
                    ${option.name}
                  </span>
                  ${optionUseSwatches(option) ? /*html*/`
                    <div class="o-swatches__swatch-options is-radio">
                      ${option.values.reduce((x, value) => {
        return x += /*html*/`
                          <div class="o-swatches__swatch-radio o-radio">
                            <input
                              type="radio"
                              id="${getSwatchValueId(option, value)}"
                              name="${p.id}-${option.name}"
                              value="${value}"
                              data-swatch-object
                              data-value="${handlize(value)}"
                              class="o-radio__input"
                              ${option.selected_value == handlize(value) ? `checked` : ``}
                            >
                            <label
                              for="${getSwatchValueId(option, value)}" class="o-radio__label"
                            >${value}</label>
                          </div>
                        `;
      }, '')}
                    </div>
                  ` : /*html*/`
                    <select class="o-swatches__select o-select" data-swatch-dropdown>
                      ${option.values.reduce((x, value) => {
        return x += /*html*/`
                          <option
                            value="${handlize(value)}"
                            ${option.selected_value == value ? `selected="selected"` : ``}
                          >
                            ${escapeString(value)}
                          </option>
                        `;
      }, '')}
                    </select>
                  `}
                </div>
              `
    }, '')}
          </div>
        `}
      </div>
    </div>
    `;
  }

  //Product ABV and stock badges
  function abvStockBadges() {
    return /*html*/`
      <div class="c-product-form__badges-wrapper">
        ${p.abv ? `<span class="c-product-form__badge o-btn no-click is-senary {{ formStyle}}">${p.abv}% ABV</span>` : ``}
        ${p.lowstock ? `<span class="c-product-form__badge o-btn no-click is-senary {{ formStyle}}">${p.lowstock}</span>` : ``}
      </div>
    `;
  }

  function optionUseSwatches(option: OptionWithValue) {
    return (handlize(option.name) === 'size' || option.values.length < 4);
  }

  //get the swatch value id
  function getSwatchValueId(option: OptionWithValue, value: string) {
    return `${p.id}-${option.position}-${handlize(value)}`;
  }

  //product variant selector
  function variantSelector() {
    return /*html*/`
      <div class="o-variant-selector no-js" data-variant-selector>
        <script type="application/json" data-variant-selector-data>
          ${JSON.stringify(p2)}
        </script>
        ${p.variants.length == 1 ? /*html*/`
          <input type="hidden" name="id" value="${v.id}" />
            ` : /*html*/`
          <select name="id" class="o-input o-variant-selector__dropdown">
            ${p.variants.reduce((x, variant) => {
      return x += /*html*/`
                <option value="${variant.id}" ${v.id === variant.id ? `selected` : ``}>
                  ${variant.title}
                </option>
              `;
    }, '')}
          </select>
        `}
      </div>
    `
  }

  //case when there is only one variant
  function hasOnlyDefaultVariant(product: ProductObjectUpdated) {
    return (
      product.variants.length === 1 &&
      product.variants[0].title.toLowerCase() === 'default title'
    );
  }
};
